import { useTranslation } from "react-i18next";
import { Integration } from "/app/src/models";
import Box from "../../../generic/components/box";
import { Formik, FormikProps, FormikHelpers } from "formik";
import { useCallback, useMemo } from "react";
import { Form, Input, Select, SubmitButton } from "formik-antd";
import { Col, Row } from "antd";
import { simpleSchemaBuilder } from "/app/src/helpers";
import { IconToolTip } from "../../../icons/IconBuilder";
import { integrationService } from "/app/src/services";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handlePromiseError } from "/app/src/helpers/api";

const ORDER_CREATION_TYPE_MANUAL = "manual";

interface ManualSettingsFormValues {
  name: string;
}

/**
 * Integration settings form component.
 * @param param0 - Props for the component.
 * @returns IntegrationSetting component.
 */
export default function IntegrationSetting({
  integration,
}: {
  integration: Integration;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const creationTypeLabel = useMemo(
    () => (
      <>
        {t("translation:order_creation_type")}
        <IconToolTip
          content={t("translation:order_builder_type_tip")}
          className="ml-1"
        />
      </>
    ),
    [t],
  );

  const { mutateAsync: updateIntegration } = useMutation({
    mutationFn: (updatedIntegration: Integration) => {
      return integrationService
        .updateSingle(integration.id, updatedIntegration)
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["integration", Number(integration.id)],
        response,
      );
    },
  });

  const handleSubmit = useCallback(
    async (
      values: ManualSettingsFormValues,
      actions: FormikHelpers<ManualSettingsFormValues>,
    ) => {
      await updateIntegration({
        id: integration.id,
        name: values.name,
      }).then(() => {
        actions.resetForm();
      });
    },
    [integration, updateIntegration],
  );

  const integrationSettingsForm: (
    props: FormikProps<ManualSettingsFormValues>,
  ) => JSX.Element = useCallback(
    ({ dirty, isSubmitting }) => (
      <Form layout="vertical">
        <Row className="pb-3">
          <Col span={8}>
            <h2 className="text-lg font-medium text-foreground !mb-0">
              {t("translation:integration_settings")}
            </h2>
          </Col>
          <Col span={8} offset={8}>
            <Row justify="end">
              <SubmitButton
                disabled={!dirty || isSubmitting}
                loading={isSubmitting}
                type="primary"
              >
                {t("translation:save")}
              </SubmitButton>
            </Row>
          </Col>
        </Row>
        <Row justify="start" gutter={16}>
          <Col span={6}>
            <Form.Item
              name="name"
              label={t("translation:integration_name")}
              hasFeedback={false}
            >
              <Input
                name="name"
                placeholder={t("translation:enter_name")}
                size="large"
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="number"
              label={creationTypeLabel}
              hasFeedback={false}
            >
              <Select disabled name="number" size="large">
                <Select.Option value="manual">
                  {t("translation:manual")}
                </Select.Option>
                <Select.Option value="automatic">
                  {t("translation:automatic")}
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    ),
    [creationTypeLabel, t],
  );

  return (
    <Box>
      <Formik
        component={integrationSettingsForm}
        initialValues={{
          name: integration.name || "",
          number: integration.number || ORDER_CREATION_TYPE_MANUAL,
        }}
        onSubmit={handleSubmit}
        enableReinitialize
        validationSchema={simpleSchemaBuilder([
          { name: "name", type: "string", required: true },
        ])}
      />
    </Box>
  );
}
